import { LegStatus, LegType, ParsedDate, ParsedTraceResult, parseTraceAvail, parseTraceResult } from '@api/Trinium/types';
import { parseIsoDate } from '@api/Trinium/types';
import { dispatchSlice } from './slice';
import { store } from '@reduxStore';
import { Location } from '../Location/types';

/** Raw response object from the Dispatch API */
export interface DispatchLegRaw {
  legId: number;
  legNo: number;
  legStatus: keyof typeof LegStatus;
  legType?: keyof typeof LegType;
  legPriority?: number;
  isActiveLeg?: boolean;
  isBulk?: boolean;
  isHazmat?: boolean;
  isInvoiced?: boolean;
  /** API flag: TRUE when the load's item status is CANCELLED or CLOSED. */
  isDisabled?: boolean;
  isCancelled?: boolean;
  isReminder?: boolean;
  reqHazmatCert?: boolean;
  schdDelvFrom?: string;
  schdDelvTo?: string;
  delvDate?: string;
  cntrId?: string;
  cntrSize?: number;
  cntrProviderId?: string;
  cntrProviderName?: string;
  chassisId?: string;
  isChassisOwned?: boolean;
  jobId: number;
  jobCat: string;
  jobCatTransType: string;
  jobDocDate?: string;
  csnId: number;
  puSealNo?: string;
  dehireId?: string;
  dehireName?: string;
  dehireCity?: string;
  dehireState?: string;
  csnNo: number;
  csnCustRefNo?: string;
  csnNotes?: string;
  csnLastFreeDate?: string;
  csnCutoffDate?: string;
  csnIsWorkflow?: boolean;
  hubId?: string;
  houseBol?: string;
  isOverweight?: boolean;
  isFirstFCL?: boolean;
  isFirstECL?: boolean;
  isLastECL?: boolean;
  isRailBilled?: boolean;
  puId?: string | null;
  puName?: string;
  puCity?: string;
  puState?: string;
  puRefNo?: string;
  delvId?: string | null;
  delvName?: string;
  delvCity?: string;
  delvState?: string;
  delvRefNo?: string;
  custId?: string;
  custName?: string;
  custEmailAddr?: string;
  custPhone?: string;
  drvId?: string | null;
  drvName?: string;
  drvType?: 'D' | 'B' | 'T';
  cdlNo?: string;
  plateNo?: string;
  hasNotes?: boolean;
  vslName?: string;
  voyage?: string;
  voyageEtaDate?: string;
  drvCellPhone?: string;
  drvEmailAddr?: string;
  /** True only when a Pickup/Delivery receipt was sent to the driver currently assigned to the leg. */
  puDelvReceiptSent?: boolean;
  tractorId?: string | null;
  relNo?: string;
  cntrType?: string;
  schdPuFrom?: string;
  schdPuTo?: string;
  puDate?: string;
  dropDate?: string;
  isLive?: boolean;
  bookingRefNo?: string;
  masterBol?: string;
  pcs?: number;
  totWgt?: number;
  wgtLb?: number;
  fullAvailDate?: string;
  emptyAvailDate?: string;
  chassisAvailDate?: string;
  // trace values
  trcChassis?: string;
  trcCntrAvail?: string;
  trcCntrHolds?: string;
  trcCntrStatus?: string;
  trcDest?: string;
  trcDestInfo?: string;
  trcETADate?: string;
  trcEvent?: string;
  trcEventState?: string;
  trcInvStatus?: string;
  trcLFD?: string;
  trcLocalDateTime?: string;
  trcLocation?: string;
  trcResult?: string;
  woContactName?: string;
  woContactEmail?: string;
  woContactPhone?: string;
}

/** Request object for the Research (Dispatch) API */
export interface DispatchLegsBySearchRequest {
  terminalNumber: string;
  jobDocStartDate: string;
  jobDocEndDate: string;
  jobId: number | null;
  custIdStartsWith: string | null;
  custNameStartsWith: string | null;
  createdByUserCode: string | null;
  cntrIdsStartsWith: string[] | null;
  masterBolStartsWith: string | null;
  drvNameStartsWith: string | null;
  custRefNoStartsWith: string | null;
  chassisIdStartsWith: string | null;
  bookingRefNoStartsWith: string | null;
  /** Filter to find primary charge approved legs */
  isPrimaryChgApproved: 'yes' | 'no' | null;
  delvStartDate: string | null;
  delvEndDate: string | null;
  schdPuStartDate: string | null;
  schdPuEndDate: string | null;
  schdDelvStartDate: string | null;
  schdDelvEndDate: string | null;
  /** Starting portion of the consignment's pickup (consignor) location code */
  consignorIdStartsWith: string | null;
  /** Starting portion of the consignment's delivery (consignee) location code */
  consigneeIdStartsWith: string | null;
}

/** Fields to exclude when converting the Raw API Dispatch Leg object to a UI Dispatch Leg object
 *  Any fields we transform should be included here so that they are omitted from the object.
 */
type ExcludedRawDispatchTypes =
  | 'schdDelvFrom'
  | 'schdDelvTo'
  | 'schdPuFrom'
  | 'schdPuTo'
  | 'csnLastFreeDate'
  | 'csnCutoffDate'
  | 'puDate'
  | 'dropDate'
  | 'docDate'
  | 'delvDate'
  | 'trcLocalDateTime'
  | 'trcETADate'
  | 'trcCntrAvail'
  | 'trcResult'
  | 'voyageEtaDate';

/** Extension of the DispatchLeg API Response to manipulate the data for the grid display */
export type DispatchLeg = Record<string, string | number | boolean | ParsedDate | LegStatus | LegType | null> &
  Omit<DispatchLegRaw, ExcludedRawDispatchTypes> & {
    /** Converting the char to full string, Import/Export/Local */
    jobCategoryTransportationType: string;
    isHazmat: boolean;
    /** Parsed ISO Dates */
    scheduledDeliveryDateFrom?: ParsedDate;
    scheduledDeliveryDateTo?: ParsedDate;
    deliverDate?: ParsedDate;
    scheduledPickupDateFrom?: ParsedDate;
    scheduledPickupDateTo?: ParsedDate;
    pickupDate?: ParsedDate;
    dropDate?: ParsedDate;
    lastFreeDayDate?: ParsedDate;
    cutoffDate?: ParsedDate;
    fullAvailableDate?: ParsedDate;
    emptyAvailableDate?: ParsedDate;
    chassisAvailableDate?: ParsedDate;
    jobDate?: ParsedDate;
    traceETADate?: ParsedDate;
    traceLocalDateTime?: ParsedDate;
    /** Availability is preferred to be a boolean but in the case we cannot determine a value, display the raw data received */
    traceCntrAvail?: string | boolean;
    /** We can parse expected Trace Results for preferred formatting, if we cannot determine the format display the raw data received */
    traceResult?: ParsedTraceResult;
    voyageEtaParseDate?: ParsedDate;
  };

/**
 * Method for Transforming the raw API response into parsed out DispatchLeg
 */
export function transformDispatchLegsResponse(result: DispatchLegRaw[]): DispatchLeg[] {
  return sortDispatchLegs(
    result.map(
      ({
        legPriority,
        jobCatTransType,
        csnLastFreeDate,
        schdDelvFrom,
        schdDelvTo,
        schdPuFrom,
        delvDate,
        puDate,
        dropDate,
        csnCutoffDate,
        fullAvailDate,
        emptyAvailDate,
        chassisAvailDate,
        jobDocDate,
        trcETADate,
        trcLocalDateTime,
        trcCntrAvail,
        trcResult,
        voyageEtaDate,
        ...item
      }) => {
        return {
          ...item,
          // Left absent, the "Hazmat Loads is No" filter compares undefined against false and hides the leg
          isHazmat: item.isHazmat ?? false,
          jobCatTransType,
          jobCategoryTransportationType: getJobCatTransTypeString(jobCatTransType),
          legPriority: legPriority ?? 2,
          lastFreeDayDate: parseIsoDate(csnLastFreeDate),
          scheduledDeliveryDateFrom: parseIsoDate(schdDelvFrom),
          scheduledDeliveryDateTo: parseIsoDate(schdDelvTo),
          deliverDate: parseIsoDate(delvDate),
          scheduledPickupDateFrom: parseIsoDate(schdPuFrom),
          scheduledPickupDateTo: parseIsoDate(item.schdPuTo),
          pickupDate: parseIsoDate(puDate),
          dropDate: parseIsoDate(dropDate),
          cutoffDate: parseIsoDate(csnCutoffDate),
          fullAvailableDate: parseIsoDate(fullAvailDate),
          emptyAvailableDate: parseIsoDate(emptyAvailDate),
          chassisAvailableDate: parseIsoDate(chassisAvailDate),
          jobDate: parseIsoDate(jobDocDate),
          traceETADate: parseIsoDate(trcETADate),
          traceLocalDateTime: parseIsoDate(trcLocalDateTime),
          traceCntrAvail: parseTraceAvail(trcCntrAvail),
          traceResult: parseTraceResult(trcResult),
          voyageEtaParseDate: parseIsoDate(voyageEtaDate)
        } as DispatchLeg;
      }
    )
  );
}

/** Method for sorting the Dispatch Legs for the Dispatch Board */
export function sortDispatchLegs(legs: DispatchLeg[]) {
  return legs.sort((a, b) => {
    // 1st by Scheduled Delivery From Date ASC
    const aSchdDelv = a.scheduledDeliveryDateFrom?.unix;
    const bSchDelv = b.scheduledDeliveryDateFrom?.unix;

    if (aSchdDelv !== bSchDelv) {
      /** Nulls go last */
      if (!aSchdDelv) {
        return 1;
      }
      if (!bSchDelv) {
        return -1;
      }

      if (aSchdDelv > bSchDelv) {
        return 1;
      } else if (aSchdDelv < bSchDelv) {
        return -1;
      }
    }

    // Next by Scheduled Delivery To Date ASC
    const aSchdDelvTo = a.scheduledDeliveryDateTo?.unix ?? 0;
    const bSchDelvTo = b.scheduledDeliveryDateTo?.unix ?? 0;

    if (aSchdDelvTo !== bSchDelv) {
      if (aSchdDelvTo > bSchDelvTo) {
        return 1;
      } else if (aSchdDelvTo < bSchDelvTo) {
        return -1;
      }
    }

    // Then by PU Location ASC
    const aPuLoc = a.puName ?? '';
    const bPuLoc = b.puName ?? '';
    if (aPuLoc > bPuLoc) {
      return 1;
    } else if (aPuLoc < bPuLoc) {
      return -1;
    }

    // Then by Customer ASC
    const aCust = a.custName ?? '';
    const bCust = b.custName ?? '';
    if (aCust > bCust) {
      return 1;
    } else if (aCust < bCust) {
      return -1;
    }

    // Then by Order # ASC
    const aOrderNo = `${String(a.jobId)}/${a.csnNo.toString().padStart(3, '0')}`;
    const bOrderNo = `${String(b.jobId)}/${b.csnNo.toString().padStart(3, '0')}`;
    if (aOrderNo > bOrderNo) {
      return 1;
    } else if (aOrderNo < bOrderNo) {
      return -1;
    }

    // Then by Leg # ASC
    const aLegNo = String(a.legNo);
    const bLegNo = String(b.legNo);
    if (aLegNo > bLegNo) {
      return 1;
    } else if (aLegNo < bLegNo) {
      return -1;
    }

    return 0;
  });
}

/** Method for sorting the Dispatch Legs when viewing by Job or Consignment for the Dispatch Board */
export function sortJobLegs(legs: DispatchLeg[]) {
  return legs.sort((a, b) => {
    // 1st by Consignment
    if (a.csnId > b.csnId) {
      return 1;
    } else if (a.csnId < b.csnId) {
      return -1;
    }

    // Then by Leg #
    if (a.legNo > b.legNo) {
      return 1;
    } else if (a.legNo < b.legNo) {
      return -1;
    }

    return 0;
  });
}

type DispatchEndpoints = typeof dispatchSlice.endpoints;

type DispatchLegsEndpoint =
  DispatchEndpoints['fetchDispatchLegs'] | DispatchEndpoints['fetchDispatchLegsByJobId'] | DispatchEndpoints['fetchDispatchLegsBySearch'];

interface DispatchCacheEntry {
  endpointName: keyof DispatchEndpoints;
  originalArgs: DispatchLegsEndpoint['Types']['QueryArg'];
}

/** Cached Dispatch queries holding the Leg, located by the tags they provide. */
function cacheEntriesHoldingLeg({ legId, jobId }: Partial<DispatchLeg>): DispatchCacheEntry[] {
  const invalidEndpoints = dispatchSlice.util.selectInvalidatedBy(store.getState(), [
    { type: 'dispatch', id: legId },
    { type: 'dispatch-by-job', id: jobId },
    { type: 'dispatch-by-search', id: legId }
  ]);

  return invalidEndpoints.map(({ endpointName, originalArgs }) => ({
    endpointName: endpointName as keyof DispatchEndpoints,
    originalArgs: originalArgs as DispatchLegsEndpoint['Types']['QueryArg']
  }));
}

function currentTerminalBoard(): DispatchCacheEntry {
  const terminalNumber = store.getState().applicationState.selectedTerminal?.number;
  if (!terminalNumber) {
    throw new Error('Could not determine the Current Terminal from Application State.');
  }

  return { endpointName: 'fetchDispatchLegs', originalArgs: terminalNumber };
}

/**
 * Applies the Leg to every Dispatch cache that already holds it, adding it where it is missing.
 *
 * Caches are located by the tags they provide, all of which are keyed on the Leg — so a Leg the API just
 * created matches nothing and is unreachable this way. Pass `isNewLeg` to also target the current
 * terminal's board, the one cache a new Leg always belongs in.
 */
export function upsertLegInDispatchCache(updatedDispatchLeg: Partial<DispatchLeg>, { isNewLeg = false } = {}) {
  const entries = cacheEntriesHoldingLeg(updatedDispatchLeg);

  if (isNewLeg) {
    const board = currentTerminalBoard();
    if (!entries.some((entry) => entry.endpointName === board.endpointName && entry.originalArgs === board.originalArgs)) {
      entries.push(board);
    }
  }

  return entries.map(({ endpointName, originalArgs }) =>
    store.dispatch(
      dispatchSlice.util.updateQueryData(endpointName, originalArgs, (draft) => {
        const leg = draft.find((x) => x.legId === updatedDispatchLeg.legId);

        if (leg) {
          Object.assign(leg, updatedDispatchLeg);
        } else {
          draft.push(updatedDispatchLeg as DispatchLeg);
        }
      })
    )
  );
}

/**
 * Applies a location's saved name and city to the legs of a job that use it, in every cached Dispatch query (board, job and search).
 * Mirrors Job:UpdateLocations on the API, which only changes legs on that job.
 */
export function applyLocationToJobLegsInDispatchCache(jobId: number, location: Location) {
  const locationValues = { name: location.name, city: location.locCity.name, state: location.locCity.state };
  const entries = dispatchSlice.util.selectInvalidatedBy(store.getState(), ['dispatch', 'dispatch-by-job', 'dispatch-by-search']);

  return entries.map(({ endpointName, originalArgs }) =>
    store.dispatch(
      dispatchSlice.util.updateQueryData(
        endpointName as keyof DispatchEndpoints,
        originalArgs as DispatchLegsEndpoint['Types']['QueryArg'],
        (draft) => {
          draft
            .filter((leg) => leg.jobId === jobId)
            .forEach((leg) => {
              if (leg.puId === location.id) {
                Object.assign(leg, { puName: locationValues.name, puCity: locationValues.city, puState: locationValues.state });
              }
              if (leg.delvId === location.id) {
                Object.assign(leg, { delvName: locationValues.name, delvCity: locationValues.city, delvState: locationValues.state });
              }
            });
        }
      )
    )
  );
}

/**
 * Function for updating all of the Dispatch Legs related to an Order in the Cache
 * @param updatedDispatchLeg
 * @returns
 */
export function updateOrderInDispatchCache(updatedDispatchLeg: Partial<DispatchLeg>) {
  const entries = cacheEntriesHoldingLeg(updatedDispatchLeg);

  return entries.map(({ endpointName, originalArgs }) =>
    store.dispatch(
      dispatchSlice.util.updateQueryData(endpointName, originalArgs, (draft) => {
        const orderLegs = draft.filter((x) => x.jobId === updatedDispatchLeg.jobId);

        if (orderLegs.length > 0) {
          orderLegs.forEach((leg) => {
            // Only update the job fields mutated (using updatedDispatchLeg directly overwrites the legID and is incorrect)
            Object.assign(leg, { bookingRefNo: updatedDispatchLeg.bookingRefNo, masterBol: updatedDispatchLeg.masterBol });
          });
        }
      })
    )
  );
}

/**
 * Batch form of upsertLegInDispatchCache. Legs are grouped by the cache entries holding them so each entry
 * is written in a single pass — one Immer produce and one store notification per cache instead of per Leg.
 * `isNewLeg` behaves as it does for the single form: every Leg is also applied to the current terminal's board.
 */
export function upsertLegsInDispatchCache(updatedDispatchLegs: Partial<DispatchLeg>[], { isNewLeg = false } = {}) {
  const legsByEntry = new Map<string, { entry: DispatchCacheEntry; legs: Partial<DispatchLeg>[] }>();
  const addToEntry = (entry: DispatchCacheEntry, updatedDispatchLeg: Partial<DispatchLeg>) => {
    const key = `${entry.endpointName}:${JSON.stringify(entry.originalArgs)}`;
    const group = legsByEntry.get(key) ?? { entry, legs: [] };
    group.legs.push(updatedDispatchLeg);
    legsByEntry.set(key, group);
  };

  updatedDispatchLegs.forEach((updatedDispatchLeg) => {
    cacheEntriesHoldingLeg(updatedDispatchLeg).forEach((entry) => {
      addToEntry(entry, updatedDispatchLeg);
    });
    // A Leg listed twice for one entry is harmless: the recipe below keys pending updates by legId
    if (isNewLeg) {
      addToEntry(currentTerminalBoard(), updatedDispatchLeg);
    }
  });

  return Array.from(legsByEntry.values()).map(({ entry: { endpointName, originalArgs }, legs }) =>
    store.dispatch(
      dispatchSlice.util.updateQueryData(endpointName, originalArgs, (draft) => {
        const pending = new Map(legs.map((leg) => [leg.legId, leg]));

        draft.forEach((row) => {
          const updatedDispatchLeg = pending.get(row.legId);
          if (updatedDispatchLeg) {
            Object.assign(row, updatedDispatchLeg);
            pending.delete(row.legId);
          }
        });

        pending.forEach((updatedDispatchLeg) => {
          draft.push(updatedDispatchLeg as DispatchLeg);
        });
      })
    )
  );
}

/**
 * Applies an update to every cached Dispatch row of one Consignment, located from any one of its Legs.
 * Consignment fields are denormalized onto each Leg row, so a save that changes one has to touch every row
 * of the order or the board shows the same Consignment two different ways. The caches are found from the
 * Leg the caller has, then every row in them carrying that csnId is patched
 */
export function updateConsignmentInDispatchCache(
  { legId, jobId, csnId }: Pick<DispatchLeg, 'legId' | 'jobId' | 'csnId'>,
  updates: Partial<DispatchLeg>
) {
  return cacheEntriesHoldingLeg({ legId, jobId }).map(({ endpointName, originalArgs }) =>
    store.dispatch(
      dispatchSlice.util.updateQueryData(endpointName, originalArgs, (draft) => {
        draft.forEach((row) => {
          if (row.csnId === csnId) {
            Object.assign(row, updates);
          }
        });
      })
    )
  );
}

/** Method for manually removing the RTK Cached Dispatch Leg of relevant Dispatch APIs  */
export function removeLegInDispatchCache({ legId, jobId }: { legId: number; jobId?: number | undefined }) {
  const invalidEndpoints = dispatchSlice.util.selectInvalidatedBy(store.getState(), [
    { type: 'dispatch', id: legId },
    { type: 'dispatch-by-job', id: jobId },
    { type: 'dispatch-by-search', id: legId }
  ]);

  return invalidEndpoints.map((currentInvalidEndpoint) => {
    type DispatchEndpoints = typeof dispatchSlice.endpoints;

    /** Get the endpoint name as a strongly typed rtk endpoint of the dispatch slice */
    const dispatchEndpointName = currentInvalidEndpoint.endpointName as keyof DispatchEndpoints;

    type InvalidatedEndpointType =
      | DispatchEndpoints['fetchDispatchLegs']
      | DispatchEndpoints['fetchDispatchLegsByJobId']
      | DispatchEndpoints['fetchDispatchLegsBySearch'];

    /** Get the original args as a strongly typed object for the specific endpoint we are handling */
    const originalArgs = currentInvalidEndpoint.originalArgs as InvalidatedEndpointType['Types']['QueryArg'];

    return store.dispatch(
      dispatchSlice.util.updateQueryData(dispatchEndpointName, originalArgs, (draft) => {
        const dispatchLegs = [...draft];
        dispatchLegs.splice(
          dispatchLegs.findIndex((x) => x.legId === legId),
          1
        );

        return dispatchLegs;
      })
    );
  });
}

/**
 * A cancelled load is returned by the Advanced Leg Search OUTER-JOINed with no dispatch leg, so its
 * leg/despatch fields are absent from the JSON entirely (`legId` comes through as `undefined`, not
 * even a `0` default). `isCancelled` is sourced from the item status, not the leg join, so it stays
 * correct for rows with no leg (e.g. legs deleted without an actual cancel).
 */
export function isCancelledLoad(row: Pick<DispatchLeg, 'isCancelled'>): boolean {
  return !!row.isCancelled;
}

export function getJobCatTransTypeString(catTransTypeChar?: string) {
  switch (catTransTypeChar) {
    case 'e':
    case 'E':
      return 'Export';
    case 'i':
    case 'I':
      return 'Import';
    case 'l':
    case 'L':
      return 'Local';
    default:
      return catTransTypeChar;
  }
}

export function isExportJob(jobCatTransType?: string) {
  return jobCatTransType === 'e' || jobCatTransType === 'E';
}
